import React, { useMemo, useCallback } from "react";
import Select, {
    StylesConfig,
} from "react-select";
import { useAppSelector, useAppDispatch } from "../../services/hooks";
import defaultCompanyIcon from "../../images/companies_layers_icon.svg";
import {
    isMultiCompanySelector,
    adminSelector,
    MultyCompaniesListSelector,
} from "../../services/selectors/user.selectors";
import { TMultyCompanyDetails } from "../../services/types/user.types";
import i18n from "../../i18n";
import { actions as userActions } from "../../services/slices/auth.slice";
import { useSwitchCompanyUserMutation } from "../../services/api/auth.api";
import { useActions } from "../../services/hooks/useActions";
import styles from "./companiesCombo.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { AppImage } from "../appMedia";

interface CompanyOption {
    value: string;
    label: string;
    company: TMultyCompanyDetails;
    logoUrl?: string | null;
}

const CompaniesCombo: React.FC = () => {

    const isMulti = useAppSelector(isMultiCompanySelector);
    const admin = useAppSelector(adminSelector);
    const companiesList = useAppSelector(MultyCompaniesListSelector);

    const { setSelectedCompany } = useActions();

    const [switchCompany, { isLoading: isSwitching }] = useSwitchCompanyUserMutation();

    const lang = i18n.language;
    const isRtl = lang === "he" || lang === "ar";

    const companies = companiesList ?? [];
    const currentCompanyId = admin?.companyId ?? admin?.CompanyId;

    const getCompanyId = useCallback((company: Record<string, unknown>): number => {
        return (company.CompanyId ?? company.companyId ?? 0) as number;
    }, []);

    const getCompanyName = useCallback((company: Record<string, unknown>): string => {
        return (i18n.dir() === "rtl" ?
            company.CompanyName ?? company.companyName ?? ""
            : company.CompanyNameEn ?? company.companyNameEn ?? company.CompanyName ?? company.companyName ?? "") as string;
    }, []);

    const getUserId = useCallback((company: Record<string, unknown>): number => {
        return (company.UserId ?? company.userId ?? 0) as number;
    }, []);

    const getCompanyLogo = useCallback((company: Record<string, unknown>): string | null => {
        return (company.ImageUrl ?? company.imageUrl ?? company.CompanyLogo ?? company.companyLogo ?? company.LogoUrl ?? company.logoUrl ?? null) as string | null;
    }, []);

    const normalizeCompany = useCallback(
        (company: Record<string, unknown>): TMultyCompanyDetails => {
            return {
                CompanyId: getCompanyId(company),
                CompanyName: getCompanyName(company),
                UserId: getUserId(company),
                ImageUrl: getCompanyLogo(company),
            };
        },
        [getCompanyId, getCompanyName, getUserId, getCompanyLogo]
    );

    const options = useMemo<CompanyOption[]>(
        () =>
            companies.map((company: TMultyCompanyDetails) => {
                const normalized = normalizeCompany(company as unknown as Record<string, unknown>);
                return {
                    value: String(normalized.CompanyId),
                    label: normalized.CompanyName || `Company ${normalized.CompanyId}`,
                    company: normalized,
                    logoUrl: normalized.ImageUrl,
                };
            }),
        [companies, normalizeCompany]
    );

    const selectedOption = useMemo<CompanyOption | null>(() => {
        if (!currentCompanyId) return options[0] ?? null;

        return (
            options.find((option) => String(currentCompanyId) === option.value) ??
            options[0] ??
            null
        );
    }, [options, currentCompanyId]);

    const handleChange = useCallback(
        async (option: CompanyOption | null) => {
            if (option?.company && admin?.guid) {
                try {
                    setSelectedCompany(option.company);
                    await switchCompany({
                        guid: admin.guid,
                        companyId: option.company.CompanyId,
                    }).unwrap();
                    // Clear all navigation states from browser history before reload
                    window.history.replaceState(null, '', window.location.pathname);
                    window.location.reload();
                } catch (error) {
                    console.error("Failed to switch company:", error);
                }
            }
        },
        [admin?.guid, switchCompany]
    );

    if (!isMulti || options.length === 0) return null;

    const customStyles: StylesConfig<CompanyOption, false> = {
        control: (provided) => ({
            ...provided,
            border: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            minHeight: 'unset',
            height: 'auto',
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0 4px',
        }),
        input: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0,
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '12px',
            boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.04)',
            marginTop: '12px',
            padding: '4px',
            backgroundColor: 'white',
            zIndex: 10000,
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#f0f2ff' : state.isFocused ? '#f8f9fa' : 'transparent',
            color: state.isSelected ? '#5d3cc7' : '#1a1a1a',
            cursor: 'pointer',
            padding: '10px 16px',
            fontSize: '15px',
            fontWeight: state.isSelected ? 600 : 500,
            borderRadius: '8px',
            transition: 'all 0.2s ease',
            ':active': {
                backgroundColor: '#eef0ff',
            },
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#1a1a1a',
            fontWeight: 700,
            fontSize: '16px',
            margin: 0,
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: '#5d3cc7',
            padding: '4px',
            ':hover': {
                color: '#4a2fb0',
            },
        }),
        indicatorSeparator: () => ({ display: 'none' }),
    };

    const currentLogoUrl = selectedOption?.logoUrl;


    return (
        <div className={`${styles.container} ${isRtl ? styles.rtl : styles.ltr}`} data-testid="companies-combo">
            <div className={styles.logoWrapper}>
                <AppImage
                    src={currentLogoUrl || defaultCompanyIcon}
                    alt={selectedOption?.label || "company"}
                    className={currentLogoUrl ? styles.companyLogo : styles.defaultIcon}
                />
            </div>
            <div style={{ flex: 1 }}>
                <Select<CompanyOption>
                    styles={customStyles}
                    options={options}
                    value={selectedOption}
                    onChange={handleChange}
                    isSearchable={false}
                    isDisabled={isSwitching}
                    isLoading={isSwitching}
                    placeholder={t("SELECT_COMPANY")}
                    menuPlacement="auto"
                    components={{ IndicatorSeparator: null }}
                    aria-label="Select company"
                />
                <span className={styles.helperText}>{t("SWITCH_COMPANY_HELPER")}</span>

            </div>
        </div>
    );
};

export default CompaniesCombo;
